import z from 'zod';
import { textTranslatableSchema, type TextTranslatableSchema } from '@app/schemas/text';
import { imageTranslatableSchema, type ImageTranslatableSchema } from '@app/schemas/image';

const themeVariable = /^var\(--[a-zA-Z][\w-]*\)$/;
const unit = '(?:px|rem|em|%)';
const lengthPattern = new RegExp(`^(?:0|(?:\\d+(?:\\.\\d+)?|\\.\\d+)${unit})$`);
const signedLengthPattern = new RegExp(`^(?:0|-?(?:\\d+(?:\\.\\d+)?|\\.\\d+)${unit})$`);
const length = z.string().max(100).refine(
  value => lengthPattern.test(value) || themeVariable.test(value),
  'Use a nonnegative CSS length such as 24px, 1rem or 100%',
);
const signedLength = z.string().max(100).refine(
  value => signedLengthPattern.test(value) || themeVariable.test(value),
  'Use a CSS length such as 24px, -1rem or 100%',
);
const size = z.union([length, z.enum(['auto', 'min-content', 'max-content', 'fit-content'])]);
const spacing = (allowAuto: boolean) => z.string().max(200).refine(value => {
  if (themeVariable.test(value)) return true;
  const parts = value.trim().split(/\s+/);
  return parts.length >= 1 && parts.length <= 4 && parts.every(part => (
    (allowAuto ? signedLengthPattern : lengthPattern).test(part) || (allowAuto && part === 'auto')
  ));
}, 'Use one to four CSS lengths');
const color = z.string().max(150).refine(value => (
  /^#(?:[\da-f]{3,4}|[\da-f]{6}|[\da-f]{8})$/i.test(value)
  || /^(?:rgb|rgba)\([\d\s.,%/]+\)$/.test(value)
  || ['transparent', 'currentColor'].includes(value)
  || themeVariable.test(value)
), 'Use a HEX/RGB/RGBA color, transparent, currentColor or a theme variable');
const position = z.string().max(100).refine(value => (
  value.trim().split(/\s+/).every(part => (
    ['left', 'right', 'top', 'bottom', 'center'].includes(part) || signedLengthPattern.test(part)
  )) && value.trim().split(/\s+/).length <= 4
), 'Use a background/object position such as center or 50% 30%');

const builderTextSchema = textTranslatableSchema.refine(
  value => Boolean(value.translations[value.fallback]?.trim()),
  { message: 'The fallback language must have a translation', path: ['translations'] },
);
const builderImageSchema = imageTranslatableSchema.refine(
  value => Boolean(value.translations[value.fallback]?.uri.trim())
    && Object.values(value.translations).every(image => Boolean(image.uri.trim())),
  { message: 'The fallback language must reference an image', path: ['translations'] },
);

export const builderStyleSchema = z.strictObject({
  width: size.optional(),
  height: size.optional(),
  minWidth: size.optional(),
  maxWidth: z.union([size, z.literal('none')]).optional(),
  minHeight: size.optional(),
  maxHeight: z.union([size, z.literal('none')]).optional(),
  padding: spacing(false).optional(),
  paddingTop: length.optional(),
  paddingRight: length.optional(),
  paddingBottom: length.optional(),
  paddingLeft: length.optional(),
  margin: spacing(true).optional(),
  marginTop: z.union([signedLength, z.literal('auto')]).optional(),
  marginRight: z.union([signedLength, z.literal('auto')]).optional(),
  marginBottom: z.union([signedLength, z.literal('auto')]).optional(),
  marginLeft: z.union([signedLength, z.literal('auto')]).optional(),
  gap: length.optional(),
  rowGap: length.optional(),
  columnGap: length.optional(),
  flexDirection: z.enum(['row', 'column']).optional(),
  alignItems: z.enum(['start', 'center', 'end', 'stretch', 'baseline']).optional(),
  justifyContent: z.enum(['start', 'center', 'end', 'space-between', 'space-around', 'space-evenly']).optional(),
  flexWrap: z.enum(['nowrap', 'wrap']).optional(),
  flexGrow: z.number().finite().min(0).max(1000).optional(),
  flexShrink: z.number().finite().min(0).max(1000).optional(),
  flexBasis: size.optional(),
  alignSelf: z.enum(['auto', 'start', 'center', 'end', 'stretch', 'baseline']).optional(),
  fontSize: length.optional(),
  fontWeight: z.number().int().min(1).max(1000).optional(),
  lineHeight: z.union([z.number().finite().min(0).max(20), length, z.literal('normal')]).optional(),
  letterSpacing: z.union([signedLength, z.literal('normal')]).optional(),
  textAlign: z.enum(['start', 'center', 'end', 'left', 'right', 'justify']).optional(),
  textTransform: z.enum(['none', 'uppercase', 'lowercase', 'capitalize']).optional(),
  textDecoration: z.enum(['none', 'underline', 'overline', 'line-through']).optional(),
  color: color.optional(),
  backgroundColor: color.optional(),
  borderWidth: spacing(false).optional(),
  borderStyle: z.enum(['none', 'solid', 'dashed', 'dotted', 'double']).optional(),
  borderColor: color.optional(),
  borderRadius: spacing(false).optional(),
  opacity: z.number().finite().min(0).max(1).optional(),
  boxShadow: z.string().max(300).refine(value => (
    value === 'none' || themeVariable.test(value)
    || (/^[\w\s.,#()%/-]+$/.test(value)
      && !/(?:url|expression|image|attr)\s*\(/i.test(value)
      && !/(?:^|[\s,(])-?(?:\d+(?:\.\d*)?|\.\d+)(?:e-?\d+)?(?:vh|vw|vmin|vmax)\b/i.test(value)
      && /(?:\d+(?:\.\d+)?(?:px|rem|em)|\b0\b)/.test(value))
  ), 'Use a CSS shadow, none or a theme variable').optional(),
  overflowX: z.enum(['visible', 'hidden', 'auto', 'scroll']).optional(),
  overflowY: z.enum(['visible', 'hidden', 'auto', 'scroll']).optional(),
  boxSizing: z.enum(['border-box', 'content-box']).optional(),
  objectFit: z.enum(['contain', 'cover', 'fill', 'none', 'scale-down']).optional(),
  objectPosition: position.optional(),
  backgroundImage: builderImageSchema.nullable().optional(),
  backgroundSize: z.union([z.enum(['cover', 'contain', 'auto']), z.string().max(100).refine(value => {
    const parts = value.trim().split(/\s+/);
    return parts.length <= 2 && parts.every(part => part === 'auto' || lengthPattern.test(part));
  }, 'Use cover, contain, auto or one to two CSS lengths')]).optional(),
  backgroundPosition: position.optional(),
  backgroundRepeat: z.enum(['no-repeat', 'repeat', 'repeat-x', 'repeat-y', 'space', 'round']).optional(),
});

z.globalRegistry.add(builderStyleSchema, { id: 'builder-style' });

export type BuilderStyle = z.infer<typeof builderStyleSchema>;
export interface BuilderState { active: boolean; disabled: boolean }

export const builderActionSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('page'), pageId: z.uuid() }),
  z.strictObject({ type: z.literal('back') }),
  z.strictObject({
    type: z.literal('service-request'),
    request: z.enum(['call-waiter', 'request-bill', 'another-round']),
    toggle: z.boolean().optional(),
  }),
]);
z.globalRegistry.add(builderActionSchema, { id: 'builder-action' });

export type BuilderAction = z.infer<typeof builderActionSchema>;

export const weatherProviderSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('open-meteo') }),
]);
export const weatherLocationSchema = z.strictObject({
  name: z.string().max(200).optional(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});
const weatherSourceFields = { provider: weatherProviderSchema, location: weatherLocationSchema };
z.globalRegistry.add(weatherProviderSchema, { id: 'builder-weather-provider' });
z.globalRegistry.add(weatherLocationSchema, { id: 'builder-weather-location' });

export const builderDataSourceSchema = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('restaurant.wifi.name') }),
  z.strictObject({ type: z.literal('restaurant.wifi.password') }),
  z.strictObject({ type: z.literal('restaurant.name') }),
  z.strictObject({ type: z.literal('table.name') }),
  z.strictObject({ type: z.literal('table.number') }),
  z.strictObject({ type: z.literal('callWaiter.main.name') }),
  z.strictObject({ type: z.literal('callWaiter.secondary.name') }),
  z.strictObject({ type: z.literal('callWaiter.tertiary.name') }),
  z.strictObject({ type: z.literal('dateTime.date') }),
  z.strictObject({ type: z.literal('dateTime.time') }),
  z.strictObject({ type: z.literal('dateTime.weekday') }),
  z.strictObject({ type: z.literal('happyHour.next.start') }),
  z.strictObject({ type: z.literal('happyHour.current.end') }),
  z.strictObject({ type: z.literal('callWaiter.main.elapsed') }),
  z.strictObject({ type: z.literal('callWaiter.secondary.elapsed') }),
  z.strictObject({ type: z.literal('callWaiter.tertiary.elapsed') }),
  z.strictObject({ type: z.literal('weather.temperature'), ...weatherSourceFields, unit: z.enum(['celsius', 'fahrenheit']).optional() }),
  z.strictObject({ type: z.literal('weather.feelsLike'), ...weatherSourceFields, unit: z.enum(['celsius', 'fahrenheit']).optional() }),
  z.strictObject({ type: z.literal('weather.condition'), ...weatherSourceFields }),
  z.strictObject({ type: z.literal('weather.humidity'), ...weatherSourceFields }),
  z.strictObject({ type: z.literal('weather.wind.speed'), ...weatherSourceFields, unit: z.enum(['kmh', 'mph']).optional() }),
]);
z.globalRegistry.add(builderDataSourceSchema, { id: 'builder-data-source' });
export type BuilderDataSource = z.infer<typeof builderDataSourceSchema>;
export type BuilderDataSourceType = BuilderDataSource['type'];
export type BuilderWeatherProvider = z.infer<typeof weatherProviderSchema>;
export type BuilderWeatherLocation = z.infer<typeof weatherLocationSchema>;
export type BuilderWeatherSource = Extract<BuilderDataSource, { type: `weather.${string}` }>;

export function isBuilderWeatherSource(source: BuilderDataSource): source is BuilderWeatherSource {
  return source.type.startsWith('weather.');
}

interface BuilderStates<T> { active?: T; disabled?: T }
interface StyleOverride { style?: BuilderStyle }
interface BuilderBase { id: string; style?: BuilderStyle }
export type BuilderNode = BuilderBase & (
  | { type: 'row' | 'col'; children: BuilderNode[]; states?: BuilderStates<StyleOverride> }
  | { type: 'button'; children: BuilderNode[]; action?: BuilderAction; states?: BuilderStates<StyleOverride> }
  | { type: 'text'; text: TextTranslatableSchema; states?: BuilderStates<StyleOverride & { text?: TextTranslatableSchema }> }
  | { type: 'data'; source: BuilderDataSource; fallback?: TextTranslatableSchema; states?: BuilderStates<StyleOverride> }
  | { type: 'image'; image: ImageTranslatableSchema; states?: BuilderStates<StyleOverride & { image?: ImageTranslatableSchema }> }
);

const base = { id: z.string().trim().min(1), style: builderStyleSchema.optional() };
const styleOverrideSchema = z.strictObject({ style: builderStyleSchema.optional() });
const statesSchema = <T extends z.ZodType>(override: T) => z.strictObject({
  active: override.optional(), disabled: override.optional(),
});

export const builderNodeSchema: z.ZodType<BuilderNode, BuilderNode> = z.lazy(() => z.discriminatedUnion('type', [
  z.strictObject({ ...base, type: z.literal('row'), children: z.array(builderNodeSchema), states: statesSchema(styleOverrideSchema).optional() }),
  z.strictObject({ ...base, type: z.literal('col'), children: z.array(builderNodeSchema), states: statesSchema(styleOverrideSchema).optional() }),
  z.strictObject({ ...base, type: z.literal('button'), children: z.array(builderNodeSchema), action: builderActionSchema.optional(), states: statesSchema(styleOverrideSchema).optional() }),
  z.strictObject({ ...base, type: z.literal('text'), text: builderTextSchema, states: statesSchema(styleOverrideSchema.extend({ text: builderTextSchema.optional() })).optional() }),
  z.strictObject({ ...base, type: z.literal('data'), source: builderDataSourceSchema, fallback: builderTextSchema.optional(), states: statesSchema(styleOverrideSchema).optional() }),
  z.strictObject({ ...base, type: z.literal('image'), image: builderImageSchema, states: statesSchema(styleOverrideSchema.extend({ image: builderImageSchema.optional() })).optional() }),
]));

z.globalRegistry.add(builderNodeSchema, { id: 'builder-node' });

export const pageViewBuilderSchema = z.strictObject({
  type: z.literal('builder.v1'),
  root: builderNodeSchema,
}).superRefine((view, context) => {
  if (!['row', 'col'].includes(view.root.type)) {
    context.addIssue({ code: 'custom', message: 'The page root must be a row or col', path: ['root', 'type'] });
  }
  const ids = new Set<string>();
  const visit = (node: BuilderNode, insideButton: boolean, path: (string | number)[]) => {
    if (ids.has(node.id)) context.addIssue({ code: 'custom', message: 'Element IDs must be unique within the page', path: [...path, 'id'] });
    ids.add(node.id);
    if (insideButton && node.type === 'button') context.addIssue({ code: 'custom', message: 'A button cannot contain another button', path: [...path, 'type'] });
    if ('children' in node) node.children.forEach((child, index) => visit(child, insideButton || node.type === 'button', [...path, 'children', index]));
  };
  visit(view.root, false, ['root']);
});

z.globalRegistry.add(pageViewBuilderSchema, { id: 'page-view-builder' });

export type PageViewBuilderSchema = z.infer<typeof pageViewBuilderSchema>;

export interface BuilderStyleField {
  name: keyof BuilderStyle;
  label: string;
  group: string;
  kind: 'text' | 'number' | 'select' | 'image';
  options?: readonly string[];
}

const fields = (group: string, names: (keyof BuilderStyle)[], kind: BuilderStyleField['kind'] = 'text'): BuilderStyleField[] => names.map(name => ({
  name, group, kind, label: name.replace(/([A-Z])/g, ' $1').replace(/^./, character => character.toUpperCase()),
}));
const select = (name: keyof BuilderStyle, group: string, options: readonly string[]): BuilderStyleField => ({
  name, group, kind: 'select', options, label: name.replace(/([A-Z])/g, ' $1').replace(/^./, character => character.toUpperCase()),
});

export const builderStyleFields: BuilderStyleField[] = [
  ...fields('Size', ['width', 'height', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight']),
  select('boxSizing', 'Size', ['border-box', 'content-box']),
  ...fields('Spacing', ['padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft', 'gap', 'rowGap', 'columnGap']),
  select('flexDirection', 'Layout', ['row', 'column']),
  select('alignItems', 'Layout', ['start', 'center', 'end', 'stretch', 'baseline']),
  select('justifyContent', 'Layout', ['start', 'center', 'end', 'space-between', 'space-around', 'space-evenly']),
  select('flexWrap', 'Layout', ['nowrap', 'wrap']),
  ...fields('Layout', ['flexGrow', 'flexShrink'], 'number'),
  ...fields('Layout', ['flexBasis']),
  select('alignSelf', 'Layout', ['auto', 'start', 'center', 'end', 'stretch', 'baseline']),
  ...fields('Text', ['fontSize', 'lineHeight', 'letterSpacing']),
  ...fields('Text', ['fontWeight'], 'number'),
  select('textAlign', 'Text', ['start', 'center', 'end', 'left', 'right', 'justify']),
  select('textTransform', 'Text', ['none', 'uppercase', 'lowercase', 'capitalize']),
  select('textDecoration', 'Text', ['none', 'underline', 'overline', 'line-through']),
  ...fields('Colors', ['color', 'backgroundColor']),
  ...fields('Border', ['borderWidth', 'borderColor', 'borderRadius']),
  select('borderStyle', 'Border', ['none', 'solid', 'dashed', 'dotted', 'double']),
  ...fields('Effects', ['opacity'], 'number'),
  ...fields('Effects', ['boxShadow']),
  select('overflowX', 'Effects', ['visible', 'hidden', 'auto', 'scroll']),
  select('overflowY', 'Effects', ['visible', 'hidden', 'auto', 'scroll']),
  select('objectFit', 'Image', ['contain', 'cover', 'fill', 'none', 'scale-down']),
  ...fields('Image', ['objectPosition']),
  ...fields('Background', ['backgroundImage'], 'image'),
  ...fields('Background', ['backgroundSize', 'backgroundPosition']),
  select('backgroundRepeat', 'Background', ['no-repeat', 'repeat', 'repeat-x', 'repeat-y', 'space', 'round']),
];
